import { ROUTES as Route } from 'constants/routes'
import {
  DefaultBodyType,
  matchRequestUrl,
  MockedResponse,
  PathParams,
  ResponseResolver,
  rest,
  RestContext,
  RestHandler,
  RestRequest
} from 'msw'
import { type Call } from './call'
import { type GatewayHandler } from './gateway_handler'
import { type Resolver } from './resolver'
import { type RouteDefinition } from './route_definition'
import { type SimpleRequestHandlerOptions } from './simple_request_handler_options'

const URL = process.env.REACT_APP_URL
const interceptionUrl = `${URL}/*`


/**
 * Enum representing HTTP verbs.
 * @example GET,POST,PUT,DELETE
 */
enum HttpVerb {
  Get = 'get',
  Post = 'post',
  Put = 'put',
  Delete = 'delete'
}

/**
 * Represents a builder for creating a Horizon Gateway MSW instance.
 * @example ```
 * HrznGatewayMswBuilder.create().get(...).build()
 * ```
 */
export class HrznGatewayMswBuilder {
  private _routeDefinitions: Record<HttpVerb, RouteDefinition[]> = {
    get: [],
    post: [],
    put: [],
    delete: []
  }

  /**
   * Creates a new instance of HrznGatewayMswBuilder.
   */
  private constructor() { }

  /**
   * Creates a new instance of HrznGatewayMswBuilder.
   * @returns {HrznGatewayMswBuilder} The created instance of HrznGatewayMswBuilder.
   */
  static create(): HrznGatewayMswBuilder {
    return new HrznGatewayMswBuilder()
  }

  /**
   * Gets the route definitions.
   * @returns {Record<HttpVerb, RouteDefinition[]>} The route definitions.
   */
  getRouteDefinitions(): Record<HttpVerb, RouteDefinition[]> {
    return this._routeDefinitions
  }

  /**
   * Includes route definitions from another HrznGatewayMswBuilder instance.
   * @param {HrznGatewayMswBuilder} other - The other HrznGatewayMswBuilder instance.
   * @returns {this} The current HrznGatewayMswBuilder instance.
   */
  include(other: HrznGatewayMswBuilder): this {
    this._routeDefinitions = Object.entries(other.getRouteDefinitions()).reduce(
      (acc, [verb, definitions]) => ({
        ...acc,
        [verb]: acc[verb as HttpVerb].concat(definitions)
      }),
      this._routeDefinitions
    )
    return this
  }

  /**
   * Adds a GET route definition.
   * @template Params - The type of the path parameters.
   * @template ResponseBody - The type of the response body.
   * @param {Route} route - The route.
   * @param {ResponseResolver<RestRequest<never, Params>, RestContext, ResponseBody>} resolver - The response resolver function.
   * @returns {this} The current HrznGatewayMswBuilder instance.
   */
  get<Params extends PathParams = PathParams, ResponseBody extends DefaultBodyType = DefaultBodyType>(
    route: Route,
    resolver: ResponseResolver<RestRequest<never, Params>, RestContext, ResponseBody>
  ): this
  get<Params extends PathParams = PathParams, ResponseBody extends DefaultBodyType = DefaultBodyType>(
    route: Route,
    path: string,
    resolver: ResponseResolver<RestRequest<never, Params>, RestContext, ResponseBody>
  ): this
  get<Params extends PathParams = PathParams, ResponseBody extends DefaultBodyType = DefaultBodyType>(
    route: Route,
    options?: SimpleRequestHandlerOptions<never, Params, ResponseBody>
  ): this
  get(route: Route, arg2: unknown, arg3?: unknown): this {
    const routeDefinition = this.parseRouteDefinitionFromArgs(route, arg2, arg3)
    this.addRouteDefinition(HttpVerb.Get, routeDefinition)
    return this
  }

  /**
   * Adds a POST route definition.
   * @template Params - The type of the path parameters.
   * @template ResponseBody - The type of the response body.
   * @param {Route} route - The route.
   * @param {ResponseResolver<RestRequest<never, Params>, RestContext, ResponseBody>} resolver - The response resolver function.
   * @returns {this} The current HrznGatewayMswBuilder instance.
   */
  post<
    RequestBody extends DefaultBodyType = DefaultBodyType,
    Params extends PathParams = PathParams,
    ResponseBody extends DefaultBodyType = DefaultBodyType
  >(route: Route, resolver: ResponseResolver<RestRequest<RequestBody, Params>, RestContext, ResponseBody>): this
  post<
    RequestBody extends DefaultBodyType = DefaultBodyType,
    Params extends PathParams = PathParams,
    ResponseBody extends DefaultBodyType = DefaultBodyType
  >(
    route: Route,
    path: string,
    resolver: ResponseResolver<RestRequest<RequestBody, Params>, RestContext, ResponseBody>
  ): this
  post<
    RequestBody extends DefaultBodyType = DefaultBodyType,
    Params extends PathParams = PathParams,
    ResponseBody extends DefaultBodyType = DefaultBodyType
  >(route: Route, options?: SimpleRequestHandlerOptions<RequestBody, Params, ResponseBody>): this
  post(route: Route, arg2?: unknown, arg3?: unknown): this {
    const routeDefinition = this.parseRouteDefinitionFromArgs(route, arg2, arg3)
    this.addRouteDefinition(HttpVerb.Post, routeDefinition)
    return this
  }

  /**
   * Adds a PUT route definition.
   * @template Params - The type of the path parameters.
   * @template ResponseBody - The type of the response body.
   * @param {Route} route - The route.
   * @param {ResponseResolver<RestRequest<never, Params>, RestContext, ResponseBody>} resolver - The response resolver function.
   * @returns {this} The current HrznGatewayMswBuilder instance.
   */
  put<
    RequestBody extends DefaultBodyType = DefaultBodyType,
    Params extends PathParams = PathParams,
    ResponseBody extends DefaultBodyType = DefaultBodyType
  >(route: Route, resolver: ResponseResolver<RestRequest<RequestBody, Params>, RestContext, ResponseBody>): this
  put<
    RequestBody extends DefaultBodyType = DefaultBodyType,
    Params extends PathParams = PathParams,
    ResponseBody extends DefaultBodyType = DefaultBodyType
  >(
    route: Route,
    path: string,
    resolver: ResponseResolver<RestRequest<RequestBody, Params>, RestContext, ResponseBody>
  ): this
  put<
    RequestBody extends DefaultBodyType = DefaultBodyType,
    Params extends PathParams = PathParams,
    ResponseBody extends DefaultBodyType = DefaultBodyType
  >(route: Route, options?: SimpleRequestHandlerOptions<RequestBody, Params, ResponseBody>): this
  put(route: Route, arg2?: unknown, arg3?: unknown): this {
    const routeDefinition = this.parseRouteDefinitionFromArgs(route, arg2, arg3)
    this.addRouteDefinition(HttpVerb.Put, routeDefinition)
    return this
  }

  /**
   * Adds a DELETE route definition.
   * @template Params - The type of the path parameters.
   * @template ResponseBody - The type of the response body.
   * @param {Route} route - The route.
   * @param {ResponseResolver<RestRequest<never, Params>, RestContext, ResponseBody>} resolver - The response resolver function.
   * @returns {this} The current HrznGatewayMswBuilder instance.
   */
  delete<
    RequestBody extends DefaultBodyType = DefaultBodyType,
    Params extends PathParams = PathParams,
    ResponseBody extends DefaultBodyType = DefaultBodyType
  >(route: Route, resolver: ResponseResolver<RestRequest<RequestBody, Params>, RestContext, ResponseBody>): this
  delete<
    RequestBody extends DefaultBodyType = DefaultBodyType,
    Params extends PathParams = PathParams,
    ResponseBody extends DefaultBodyType = DefaultBodyType
  >(
    route: Route,
    path: string,
    resolver: ResponseResolver<RestRequest<RequestBody, Params>, RestContext, ResponseBody>
  ): this
  delete<
    RequestBody extends DefaultBodyType = DefaultBodyType,
    Params extends PathParams = PathParams,
    ResponseBody extends DefaultBodyType = DefaultBodyType
  >(route: Route, options?: SimpleRequestHandlerOptions<RequestBody, Params, ResponseBody>): this
  delete(route: Route, arg2?: unknown, arg3?: unknown): this {
    const routeDefinition = this.parseRouteDefinitionFromArgs(route, arg2, arg3)
    this.addRouteDefinition(HttpVerb.Delete, routeDefinition)
    return this
  }

  /**
   * Builds the GatewayHandler instance.
   * @returns {GatewayHandler} The built GatewayHandler instance.
   */
  build(): GatewayHandler {
    const copiedRouteDefinitions = this.copyRouteDefinitions()
    let calls: Call[] = []
    // This is inspired stronly by
    // https://github.com/AndersDJohnson/msw-expect
    const gatewayHandler: GatewayHandler = Object.assign(
      rest.all(interceptionUrl, (req, res, ctx) => {
        return this.createHandler(copiedRouteDefinitions[req.method.toLowerCase() as HttpVerb], calls)(req, res, ctx)
      }
      ),
      {
        getCallsTo: route => calls.filter(x => x.route === route),
        getMostRecentRequestTo: route => {
          const calls = gatewayHandler.getCallsTo(route)
          return calls[calls.length - 1]?.request
        },
        getMostRecentResponseFrom: route => {
          const calls = gatewayHandler.getCallsTo(route)
          return calls[calls.length - 1]?.response
        },
        reset: () => {
          calls = []
          return gatewayHandler
        },
        getRecentRequestsList(): Call[] | undefined {
          return calls
        }
      } as Omit<GatewayHandler, keyof RestHandler>
    )

    return gatewayHandler
  }

  /**
   * Creates a request handler for processing incoming requests.
   * @param routeDefinitions The route definitions to match incoming requests against.
   * @param calls The array to store information about processed calls.
   * @returns A request handler function.
   */
  private createHandler(routeDefinitions: RouteDefinition[], calls: Call[]): Resolver {
    return async (req, res, ctx) => {
      const route = req.url.searchParams.get('route')
      const routeDefinition = routeDefinitions.find(x => x.route === route)

      if (!routeDefinition) return

      const { matches, params } = matchRequestUrl(
        req.url,
        `${URL}${routeDefinition.path.startsWith('/') ? '' : '/'}${routeDefinition.path}`
      )

      if (!matches) return

      const mockedResponse = (await routeDefinition.resolver(
        { ...req, params: params ?? req.params },
        res,
        ctx
      )) as MockedResponse<DefaultBodyType>

      if (!mockedResponse) return

      calls.push({
        route: routeDefinition.route,
        request: req,
        response: this.copyMockedResponseAndParseBody(mockedResponse)
      })
      return mockedResponse
    }
  }

  /**
   * Copies the mocked response and parses the response body if it is in JSON format.
   * @param mockedResponse The mocked response to copy and parse.
   * @returns The copied and parsed mocked response.
   */
  private copyMockedResponseAndParseBody(mockedResponse: MockedResponse<DefaultBodyType>): Call['response'] {
    const copiedMockedResponse: Call['response'] = {
      ...mockedResponse,
      rawBody: mockedResponse.body
    }

    if (copiedMockedResponse.body && copiedMockedResponse.headers?.get('content-type')?.includes('json')) {
      try {
        copiedMockedResponse.body = JSON.parse(copiedMockedResponse.body as string)
      } catch {
        // fail silently
      }
    }

    return copiedMockedResponse
  }

  /**
   * Copies the route definitions.
   * @returns The copied route definitions.
   */
  private copyRouteDefinitions() {
    return Object.entries(this._routeDefinitions).reduce(
      (acc, [verb, definitions]) => ({ ...acc, [verb]: definitions.slice() }),
      {} as typeof this._routeDefinitions
    )
  }

  /**
   * Adds a new route definition to the gateway.
   * @param verb The HTTP verb for the route.
   * @param routeDefinition The route definition to add.
   */
  private addRouteDefinition(verb: HttpVerb, routeDefinition: RouteDefinition): void {
    this._routeDefinitions[verb].push(routeDefinition)
  }

  /**
   * Parses a route definition from the provided arguments.
   * @param route The route path.
   * @param arg2 Optional argument representing the path or resolver function.
   * @param arg3 Optional argument representing the resolver function.
   * @returns The parsed route definition.
   */
  private parseRouteDefinitionFromArgs(route: Route, arg2?: unknown, arg3?: unknown): RouteDefinition {
    if (typeof arg2 === 'function') {
      return {
        route,
        path: '/',
        resolver: arg2 as Resolver
      }
    }

    if (typeof arg3 === 'function') {
      return {
        route,
        path: arg2 as string,
        resolver: arg3 as Resolver
      }
    }

    const options = (arg2 as SimpleRequestHandlerOptions | undefined) ?? {}
    return {
      route,
      path: options.path ?? '/',
      resolver: (req, res, ctx) => {
        return res(
          ctx.status(options.status ?? 200),
          options.delay ? ctx.delay(typeof options.delay === 'number' ? options.delay : 5_000) : x => x,
          ctx.json(typeof options.data === 'function' ? options.data(req, res, ctx) : options.data)
        )
      }
    }
  }
}
